/**
 * agentActions.js — Action definitions and builders for the Onboarding Agent.
 *
 * Actions are structured objects that describe what the agent wants to do.
 * The UI layer renders them with confirmation before applying.
 *
 * Action types:
 *   FILL_FIELDS    — Fill one or more form fields
 *   SELECT_SPORT   — Select a sport chip/button
 *   SELECT_MODULES — Select service modules
 *   SEARCH_CLUB    — Trigger club search for results linking
 *   NAVIGATE_STEP  — Move to a wizard step
 *   SUMMARIZE      — Show summary of current state
 *   SHOW_PENDING   — List missing required fields
 *   ADD_CONTACT    — Pre-fill a contact entry (wizard step 2)
 *   ADD_SPORT_ENTRY— Pre-fill a sport structure entry (wizard step 3)
 *   ADD_INVITE     — Pre-fill an invite entry (wizard step 6)
 */

/* ══════════════════════════════════════════════════════════
   ACTION TYPES
   ══════════════════════════════════════════════════════════ */

export const ActionType = {
  FILL_FIELDS: 'FILL_FIELDS',
  SELECT_SPORT: 'SELECT_SPORT',
  SELECT_MODULES: 'SELECT_MODULES',
  SEARCH_CLUB: 'SEARCH_CLUB',
  NAVIGATE_STEP: 'NAVIGATE_STEP',
  SUMMARIZE: 'SUMMARIZE',
  SHOW_PENDING: 'SHOW_PENDING',
  ADD_CONTACT: 'ADD_CONTACT',
  ADD_SPORT_ENTRY: 'ADD_SPORT_ENTRY',
  ADD_INVITE: 'ADD_INVITE',
};

/* ══════════════════════════════════════════════════════════
   ACTION BUILDERS
   ══════════════════════════════════════════════════════════ */

/**
 * Fill one or more fields in the current form.
 * @param {Object} fields - { fieldName: value, ... }
 * @param {string} reason - Why the agent wants to fill these
 */
export function fillFields(fields, reason) {
  return {
    type: ActionType.FILL_FIELDS,
    fields,
    reason,
    confirmLabel: 'Preencher campos',
  };
}

export function selectSport(sportId, reason) {
  return {
    type: ActionType.SELECT_SPORT,
    sportId,
    reason,
    confirmLabel: `Selecionar esporte`,
  };
}

export function selectModules(moduleCodes, reason) {
  return {
    type: ActionType.SELECT_MODULES,
    moduleCodes,
    reason,
    confirmLabel: `Selecionar ${moduleCodes.length} módulos`,
  };
}

export function searchClub(query, reason) {
  return {
    type: ActionType.SEARCH_CLUB,
    query,
    reason,
    confirmLabel: 'Buscar clube',
  };
}

export function navigateStep(step, reason) {
  return {
    type: ActionType.NAVIGATE_STEP,
    step,
    reason,
    confirmLabel: `Ir para etapa ${step}`,
  };
}

export function summarize() {
  return { type: ActionType.SUMMARIZE };
}

export function showPending() {
  return { type: ActionType.SHOW_PENDING };
}

export function addContact(contactData, reason) {
  return {
    type: ActionType.ADD_CONTACT,
    contactData,
    reason,
    confirmLabel: 'Adicionar contato',
  };
}

export function addSportEntry(sportData, reason) {
  return {
    type: ActionType.ADD_SPORT_ENTRY,
    sportData,
    reason,
    confirmLabel: 'Adicionar modalidade',
  };
}

export function addInvite(inviteData, reason) {
  return {
    type: ActionType.ADD_INVITE,
    inviteData,
    reason,
    confirmLabel: 'Adicionar convite',
  };
}

/* ══════════════════════════════════════════════════════════
   FIELD DISPLAY LABELS (for confirmation UI)
   ══════════════════════════════════════════════════════════ */

export const FIELD_LABELS = {
  // Register
  full_name: 'Nome completo',
  email: 'Email',
  password: 'Senha',
  confirmPassword: 'Confirmar senha',
  primary_sport: 'Esporte principal',
  organization_name: 'Nome do clube',
  phone: 'Telefone',
  country: 'País',
  city: 'Cidade',
  // Wizard step 1
  legal_name: 'Nome oficial',
  trade_name: 'Nome fantasia',
  org_type: 'Tipo de organização',
  cnpj: 'CNPJ',
  state: 'Estado',
  website: 'Website',
  instagram: 'Instagram',
  // Wizard step 3
  sport_name: 'Esporte',
  category: 'Categoria',
  num_athletes: 'Nº de atletas',
  num_coaches: 'Nº de treinadores',
  // Wizard step 6
  invite_email: 'Email do convidado',
  invite_name: 'Nome do convidado',
  invite_role: 'Papel',
};

/** Get human-readable label for a field */
export function getFieldLabel(key) {
  return FIELD_LABELS[key] || key;
}

/* ══════════════════════════════════════════════════════════
   SPORT / ORG / MODULE LABELS
   ══════════════════════════════════════════════════════════ */

export const SPORT_LABELS = {
  swimming: 'Natação', football: 'Futebol', basketball: 'Basquete',
  volleyball: 'Vôlei', athletics: 'Atletismo', judo: 'Judô',
  water_polo: 'Polo Aquático', gymnastics: 'Ginástica', other: 'Outro',
};

export const ORG_TYPE_LABELS = {
  club: 'Clube', academy: 'Academia/Escola', team: 'Equipe/Time',
  federation: 'Federação', school: 'Projeto Social', other: 'Outro',
};

export function getSportLabel(id) { return SPORT_LABELS[id] || id; }
export function getOrgTypeLabel(id) { return ORG_TYPE_LABELS[id] || id; }
